<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { onMounted, ref, computed, onBeforeUnmount, watch } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import Swal from "sweetalert2";

import { Button as KButton } from '@progress/kendo-vue-buttons'
import { useCookies } from 'vue3-cookies'
import { GetCartByCustomer,GetCartFromLocalStorageOrApi,GuestAddToCart,
  ShippingAddressAddToCart,GetCurrentCartFromLocalStorage,
  SaveCartToLocalStorage, ExcludeOrderTypeFromLocalStorage } from '../../services/CartService.ts'
import { CartCreate, CartPaymentMethodGetList } from '../../api/Cart.ts'
import { GetOrderTypeCountry } from '../../services/CommonService.ts'
import { handleError, AlertCartSubmitResponse } from '../../services/ErrorService.ts'
import type { IAoeCart, ICartResponse } from '../../types/CartTypes'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { GetFullProductImageDictionary } from '../../services/SkuService.ts'
import { GetConsultantFromLocalStorageOrApi } from '../../services/ConsultantService'
import { CartSubmit } from '../../api/Cart.ts'
import {
  getContactDetails
} from '../../api/AddressBook'
import AoeTabbedProductEntry from '../Controls/AoeTabbedProductEntry.vue'
import AoeCartItemGrid from '../Controls/AoeCartItemGrid.vue'
import AoeShippingControl from '../Controls/AoeShippingControl.vue'
import AoeOrderTotalsSummary from '../Controls/AoeOrderTotalsSummary.vue'
import AoePoePriceCatTotals from '../Controls/AoePriceCategoryTotals.vue'
import AoePaymentControl from '../Controls/AoePaymentControl.vue'
import AoeDiscountControl from '../Controls/AoeDiscountControl.vue'
import { GetBeeNumberAuthenicatedUser, GetOrderReceiptUrl } from '../../stores/SettingsStore.ts'
import type {  IShippingAddress } from '../../types/CartTypes.js'


const props = defineProps<{
  ordertypeid: string
  istwosteps: boolean
}>()

const logger = useLogger()
setStoreLogger(logger)
logger.debug('AoeConsultantOrderEntry.vue')

const orderTypeId = ref<string>('')
const countryCode = ref<string>('')
const selectedTab = ref(0);
const selectedProductEntryTab = ref(0)
const selectedProductEntryCategory = ref('')
const customerBeeNumber = ref<number>(0)
const orderReceiptUrl = ref<string>('')

const cartResponse = ref<ICartResponse | null>(null)
const cart = ref<IAoeCart | null>(null)
const productImageUrls = ref<Record<string, string>>({})
const showEntryStep1 = ref<boolean>(true)
const showEntryStep2 = ref<boolean>(false)
const beeNumber = ref<number | null>(0)
const contactOrder = ref<string | null>(null)
const contactCustomer = ref<string | null>(null)

// Shipping control ref for validation
const shippingControlRef = ref<InstanceType<typeof AoeShippingControl> | null>(null)

// Guards against out-of-order responses: update-cart can fire in quick succession
// (e.g. saving an address then switching ship-to-type), and a slower earlier
// request resolving after a faster later one must not overwrite the newer cart state.
let latestLoadCartRequestId = 0

// ContactCart() pulls the contact's address-book default onto the cart. It must only
// run once, on the initial load for a contact order - not on every LoadCart() triggered
// by updateCartHandler (e.g. after the user edits/validates a new shipping address),
// or it will silently overwrite the address the user just entered with the contact's
// stored default (the flash-to-old-address bug).
let contactAddressApplied = false

// This page always loads its cart from the server (GetCartByCustomer), so keep it out of the
// shared local storage the shop cart page reads - see ExcludeOrderTypeFromLocalStorage.
ExcludeOrderTypeFromLocalStorage(props.ordertypeid)

onMounted(async () => {
  logger.debug('onMounted aoeConsultantOrderEntry')

  SetIsLoading(true)
  orderReceiptUrl.value = await GetOrderReceiptUrl()
  beeNumber.value = await GetBeeNumberAuthenicatedUser()
  if (beeNumber.value) {
    const consultant = await GetConsultantFromLocalStorageOrApi(beeNumber.value)
    countryCode.value = consultant?.countryCode ?? ''
  }

  // Set orderTypeId from prop if provided
  if (props.ordertypeid) {
    orderTypeId.value = props.ordertypeid
  }

  orderTypeId.value = await GetOrderTypeCountry(orderTypeId.value)
  ExcludeOrderTypeFromLocalStorage(orderTypeId.value)
  GetBeeNumberAuthenicatedUser().then((resp) =>
  {
    if (resp != null)
    {
      const urlParams = new URLSearchParams(window.location.search)

      contactCustomer.value = urlParams.get('contactBeeNumber')
      contactOrder.value = urlParams.get('contact')
        if (contactCustomer.value) {
          customerBeeNumber.value = parseInt(contactCustomer.value)
        } else {
          customerBeeNumber.value = resp
        }

      LoadCart()
    }
  })

})

async function continueShopping(event: MouseEvent) {
  logger.debug('continueShopping', event)
  showEntryStep1.value = true
  showEntryStep2.value = false
}

async function LoadCart() {
  logger.debug('LoadCart start')

  const requestId = ++latestLoadCartRequestId

  try {
    const response = await GetCartByCustomer({
      beeNumberPayee: beeNumber.value,
      beeNumberCustomer: customerBeeNumber.value,
      orderTypeId: orderTypeId.value
    })

    // A newer update-cart request was issued while this one was in flight -
    // its response is stale, so discard it to avoid clobbering fresher cart state.
    if (requestId !== latestLoadCartRequestId) {
      logger.debug('LoadCart discarding stale response', requestId, latestLoadCartRequestId)
      return
    }
    cartResponse.value = response

    if (cartResponse.value) {
      cart.value = cartResponse.value.item
      const urls = await GetProductImageUrls(cart.value)

      if (requestId !== latestLoadCartRequestId) {
        logger.debug('LoadCart discarding stale response after image fetch', requestId, latestLoadCartRequestId)
        return
      }
      productImageUrls.value = urls
    }

    if(contactOrder.value && cart.value && !contactAddressApplied) {
      contactAddressApplied = true
      await ContactCart()

      if (requestId !== latestLoadCartRequestId) {
        logger.debug('LoadCart discarding stale response before RefreshCart', requestId, latestLoadCartRequestId)
        return
      }
      await RefreshCart(requestId)
    }

    SetIsLoading(false)
  } catch (error: unknown) {
    logger.error('LoadCart error:', error)
    SetIsLoading(false)

    // Check for SingleUseOrderTypeRoleMissingException
    const axiosError = error as { response?: { data?: { responseStatus?: { errorCode?: string } } } }
    if (axiosError?.response?.data?.responseStatus?.errorCode === 'SingleUseOrderTypeRoleMissingException') {
      await Swal.fire({
        title: t('alerts.SingleUseOrderLimitTitle'),
        text: t('alerts.SingleUseOrderLimitMessage'),
        icon: 'warning',
        confirmButtonText: t('buttons.Okay'),
      })
    } else {
      // Handle other errors using the standard error handler
      await handleError(error, t)
    }
  }

  logger.debug('LoadCart done')
}

async function RefreshCart(requestId: number) {
  const response = await GetCartByCustomer({
    beeNumberPayee: beeNumber.value,
    beeNumberCustomer: customerBeeNumber.value,
    orderTypeId: orderTypeId.value
  })

  if (requestId !== latestLoadCartRequestId) {
    logger.debug('RefreshCart discarding stale response', requestId, latestLoadCartRequestId)
    return
  }
  cartResponse.value = response

  if (cartResponse.value) {
    cart.value = cartResponse.value.item
    const urls = await GetProductImageUrls(cart.value)

    if (requestId !== latestLoadCartRequestId) {
      logger.debug('RefreshCart discarding stale response after image fetch', requestId, latestLoadCartRequestId)
      return
    }
    productImageUrls.value = urls
  }
}

async function ContactCart() {

  if (contactOrder.value && cart.value) {
    const contactDetails = await getContactDetails(parseInt(contactOrder.value));
    
    const hasValidShippingAddress = contactDetails.shippingAddress && 
      contactDetails.shippingAddress.addressLine1 && 
      contactDetails.shippingAddress.city && 
      contactDetails.shippingAddress.countryCode;
    
    if (hasValidShippingAddress) {
      logger.debug('Shipping address found, adding to cart');
      const shippingAddress: IShippingAddress = {
        firstName: contactDetails.shippingAddress.firstName || '',
        lastName: contactDetails.shippingAddress.lastName || '',
        addressLine1: contactDetails.shippingAddress.addressLine1,
        addressLine2: contactDetails.shippingAddress.addressLine2,
        houseNumber: contactDetails.shippingAddress.houseNumber,
        city: contactDetails.shippingAddress.city,
        stateCode: contactDetails.shippingAddress.stateCode,
        postalCode: contactDetails.shippingAddress.postalCode,
        countryCode: contactDetails.shippingAddress.countryCode,
      };

      logger.debug('ShippingAddressAddToCart');
      await ShippingAddressAddToCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        address: shippingAddress,
        skipAddressValidation: true,
      });
    } else {
      logger.debug('No valid shipping address found for contact, user can add one during checkout');
    }
  }
}

async function GetProductImageUrls(cart: IAoeCart) : Promise<Record<string, string>>
{
  const uniqueProductCodes = Array<string>()
  for(const cartItem of cart.items) {
    if (!uniqueProductCodes.includes(cartItem.productCode)) {
      uniqueProductCodes.push(cartItem.productCode)
    }
  }
  if (cart.earnedDiscounts != undefined)
  {
    for(const earnedDisc of cart.earnedDiscounts) {
      if (earnedDisc.items != undefined) {
        for(const earnedDiscItem of earnedDisc.items) {
          if (!uniqueProductCodes.includes(earnedDiscItem.productCode)) {
            uniqueProductCodes.push(earnedDiscItem.productCode)
          }
        }
      }
    }
  }
  const pIUrls = await GetFullProductImageDictionary(uniqueProductCodes)
  return pIUrls
}

function updateCartHandler(event: MouseEvent) {
  logger.debug('updateCartHandler', 'AoeConsultantOrderEntry', event, selectedTab)
  LoadCart()
}

function updateProductEntryTabHandler(selectedProdEntryTab: number) {
  logger.debug('updateProductEntryTabHandler', 'AoeConsultantOrderEntry', selectedProdEntryTab)
  selectedProductEntryTab.value = selectedProdEntryTab
}

function updateSelectedCategoryHandler(selectedProdEntryCat: string) {
  logger.debug('updateSelectedCategoryHandler', 'AoePartyOrderEntry', selectedProdEntryCat)
  selectedProductEntryCategory.value = selectedProdEntryCat
}

function proceedToCheckoutHandler() {
  logger.debug('proceedToCheckoutHandler', 'AoeConsultantOrderEntry')
  showEntryStep1.value = false
  showEntryStep2.value = true
}

function submitMasterOrder(event: MouseEvent) {
  logger.debug('submitMasterOrder', event)

  if (cart.value == undefined) return

  // Validate shipping control fields before submitting
  if (shippingControlRef.value && !shippingControlRef.value.validate()) {
    return // Don't proceed with submit if shipping validation fails
  }

  SetIsLoading(true)
    CartSubmit(cart.value?.aoeCartIdToken).then(async (resp) => {
      logger.debug('CartSubmit', resp)
      if (resp.aoeOrderId)
      {
        Swal.fire({
          title: t("alerts.CartSubmitSuccessTitle"),
          text: t("alerts.CartSubmitSuccess", [
          ]),
          icon: "success",
          confirmButtonText: t("buttons.Okay"),
        }).then((result) => {
          if (result.isConfirmed) {
            SetIsLoading(false)

            //redirect to order summary page
            if (resp.aoeOrderIdToken)
            {
              const receiptURL = orderReceiptUrl.value + '?id=' + resp.aoeOrderIdToken;
              window.location.href = receiptURL
            }
            else
            {
              window.location.href = window.location.href
            }
          }
        });
      }
      else
      {
        AlertCartSubmitResponse(t, resp.item)
        SetIsLoading(false)
      }
    }).catch(async error => {
        SetIsLoading(false)
        await handleError(error, t)
      })
}

</script>

<template>
  <div class="order-entry-div">
    <!-- Surfaces items the server auto-removed from this cart because they're no longer
         available (expired/discontinued, or no currently-effective price row) - see
         AOECartBO.PruneUnavailableCartItems. Shown regardless of single-page/two-step layout
         since either can trigger a load that drops an item. -->
    <div v-if="cart?.validationWarnings && cart.validationWarnings.length > 0" class="row mt-3">
      <div class="col-12">
        <div class="validation-warnings-container" data-testid="validation-warnings">
          <div
            v-for="(warning, index) in cart.validationWarnings"
            :key="index"
            class="validation-warning-item"
          >
            <span class="warning-icon">⚠️</span>
            <span class="warning-text">{{ t(`cart.warnings.${warning.warningCode}`, warning.attributes || {}) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!props.istwosteps" class="single-page-order-entry">
      <div v-if="cart" class="row mt-3">
        <div class="col-12 tabbed-product-entry-div">
          <AoeTabbedProductEntry
            :cart="cart"
            :ordertypeid="props.ordertypeid"
            :showheader="true"
            :entryselectedtab="selectedProductEntryTab"
            :entryselectedcategory="selectedProductEntryCategory"
            @update-cart="updateCartHandler"
            @update-product-entry-tab="updateProductEntryTabHandler"
            @update-selected-category="updateSelectedCategoryHandler">
          </AoeTabbedProductEntry>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 cartItemGridDiv">
          <AoeCartItemGrid
            :cart="cart"
            :productimageurls="productImageUrls"
            :editable="true"
            @update-cart="updateCartHandler">
          </AoeCartItemGrid>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 party-discount-control-div">
          <AoeDiscountControl
            :cart="cart"
            :productimageurls="productImageUrls"
            @update-cart="updateCartHandler">
          </AoeDiscountControl>
        </div>
      </div>
      <div class="row mt-3 shipping-control-row">
        <div class="col-12 shipping-control-div">
          <AoeShippingControl
           v-if="cart"
            ref="shippingControlRef"
            :selectedcountry="countryCode"
            :cart="cart"
            :showshiptotype="false"
            @update-cart="updateCartHandler">
          </AoeShippingControl>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 aoe-payment-control-div">
          <AoePaymentControl
            v-if="cart"
            :carts="cart ? [cart] : null"
            :showdeletebutton="true"
            :showinvoice="true"
            :allowaddpayment="true"
            @update-cart="updateCartHandler">
          </AoePaymentControl>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12 col-md-6 aoe-order-summary-div">
          <AoePoePriceCatTotals
            :cart="cart"
            :orderTypeId="orderTypeId">
          </AoePoePriceCatTotals>
        </div><div class="col-12 col-md-6 aoe-order-summary-div">
          <AoeOrderTotalsSummary
            :cart="cart">
          </AoeOrderTotalsSummary>
        </div>
      </div>
      <div v-if="cart" class="row mt-3">
        <div ref="shipNowDiv" class="col-12 aoe-order-ship-now-div">
          <span style="display: inline-block; pointer-events: auto;">
            <KButton
              :rounded="'large'"
              class="baseBtn primaryBtn submit-button"
              @click="submitMasterOrder($event)">
              {{ t('consultantOrderEntry.SubmitButton') }}
            </KButton>
          </span>
        </div>
      </div>
    </div>
    <div v-else class="multi-page-order-entry">
      <div v-if="showEntryStep1">
        <div v-if="cart" class="row mt-3">
          <div class="col-12 tabbed-product-entry-div">
            <AoeTabbedProductEntry
              :cart="cart"
              :ordertypeid="props.ordertypeid"
              :showheader="true"
              :entryselectedtab="selectedProductEntryTab"
              :entryselectedcategory="selectedProductEntryCategory"
              :istwosteps="true"
            :productimageurls="productImageUrls"
              @update-cart="updateCartHandler"
              @update-product-entry-tab="updateProductEntryTabHandler"
              @update-selected-category="updateSelectedCategoryHandler"
              @proceed-to-checkout="proceedToCheckoutHandler">
            </AoeTabbedProductEntry>
          </div>
        </div>
      </div>
      <div v-if="showEntryStep2">
        <div class="row mt-3">
          <div ref="shipNowDiv" class="col-12 aoe-order-ship-now-div">
            <span style="display: inline-block; pointer-events: auto;">
              <KButton
                :rounded="'large'"
                class="baseBtn primaryBtn continue-shopping-button"
                @click="continueShopping($event)">
                {{ t('consultantOrderEntry.ContinueShoppingButton') }}
              </KButton>
            </span>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 cartItemGridDiv">
            <AoeCartItemGrid
              :cart="cart"
              :productimageurls="productImageUrls"
              :editable="true"
              @update-cart="updateCartHandler">
            </AoeCartItemGrid>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 party-discount-control-div">
            <AoeDiscountControl
              :cart="cart"
              :productimageurls="productImageUrls"
              @update-cart="updateCartHandler">
            </AoeDiscountControl>
          </div>
        </div>
        <div class="row mt-3 shipping-control-row">
          <div class="col-12 shipping-control-div">
            <AoeShippingControl
              ref="shippingControlRef"
              :selectedcountry="countryCode"
              :cart="cart"
              :showshiptotype="false"
              @update-cart="updateCartHandler">
            </AoeShippingControl>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 aoe-payment-control-div">
            <AoePaymentControl
              :carts="cart ? [cart] : null"
              :showdeletebutton="true"
              :showinvoice="true"
              :allowaddpayment="true"
              @update-cart="updateCartHandler">
            </AoePaymentControl>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12 col-md-6 aoe-order-summary-div">
            <AoePoePriceCatTotals
              :cart="cart"
              :orderTypeId="orderTypeId">
            </AoePoePriceCatTotals>
          </div><div class="col-12 col-md-6 aoe-order-summary-div">
            <AoeOrderTotalsSummary
              :cart="cart">
            </AoeOrderTotalsSummary>
          </div>
        </div>
        <div class="row mt-3">
          <div ref="shipNowDiv" class="col-12 aoe-order-ship-now-div">
            <span style="display: inline-block; pointer-events: auto;">
              <KButton
                :rounded="'large'"
                class="baseBtn primaryBtn submit-button"
                @click="submitMasterOrder($event)">
                {{ t('consultantOrderEntry.SubmitButton') }}
              </KButton>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.error-consolidated-address
{
  color: red;
}

.validation-warnings-container {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: $radius-md;
  padding: $spacing-4;
}

.validation-warning-item {
  display: flex;
  align-items: flex-start;
  gap: $spacing-2;
  padding: $spacing-2 0;

  &:not(:last-child) {
    border-bottom: 1px solid #ffe69c;
  }
}

.warning-icon {
  font-size: 1.25rem;
  line-height: 1;
  flex-shrink: 0;
}

.warning-text {
  color: #856404;
  font-size: 0.9375rem;
  line-height: 1.5;
  font-weight: 500;
}

.hostTemplate .single-line-auto-complete-row,
.guestTemplate .single-line-auto-complete-row
{
  margin-top: 1rem !important;
}

.party-order-entry-tabs .k-tabstrip-content
{
  width: 85%;
}

.party-order-entry-tabs .k-animation-container
{
  width: 100%;
}

.party-order-entry-tabs .k-tabstrip-items-wrapper
{
  width: 15%;
}

.party-order-entry-section-header-div
{
  @include typography-body-large;
  @include font-weight-semibold;
  padding: $spacing-3 $spacing-4;
  background-color: transparent;
  border-bottom: 2px solid var(--primary-text-color, var(--vt-c-teal-dark));
  margin-bottom: 0;
}

.grid-delete .k-svg-icon,
.grid-delete .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.grid-delete .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,
.percent-off-item-delete-icon .k-svg-icon,
.percent-off-item-delete-icon.k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.percent-off-item-delete-icon.k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 30px;
    height: 30px;
}


.aoe-cart-item-grid .k-grid-content,
.aoe-payment-grid .k-grid-content {
  overflow-y: hidden;
}

.aoe-cart-item-grid .k-grid-header,
.aoe-payment-grid .k-grid-header
{
  padding: inherit !important;
  padding-inline-end: inherit;
}

.aoe-order-ship-now-div,
.aoe-order-submit-div
{
  text-align: right;
}

.party-order-entry-tab,
.party-order-entry-tab-header
{
  @include typography-body;
  padding: $spacing-2-5;
}

.party-order-entry-tab-header
{
  @include font-weight-semibold;
}

.consolidated-address-title
{
  @include typography-body-large;
  @include font-weight-semibold;
  text-align: center;
  padding-bottom: 10px;
}

.consolidated-address
{
  @include typography-body;
  text-align: center;
}

.tabbed-product-entry-div .k-tabstrip-items-wrapper,
.tabbed-product-entry-div .k-animation-container,
.tabbed-product-entry-div .k-tabstrip-content
{
  width: 100%;
}

.tabbed-product-entry-div .k-tabstrip-items-wrapper
{
  @include typography-h4;
  @include font-weight-semibold;
}

.tabbed-product-entry-div .k-item
{
  padding: $spacing-2-5;
}


.inventory-stop-sell
{
  color: red;
}

.inventory-available
{
  color: green;
}

.inventory-low-stock
{
  color: yellow;
}

.inventory-out-of-stock
{
  color: orange;
}

.product-grid-stock-indicator .k-svg-icon,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,
.stock-indicator-key-div .k-svg-icon,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 40px;
    height: 40px;
}

.aoe-change-address-popup-content
{
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);
}

.closePopup
{
  text-align: right;
}

.aoe-change-address-popup-content-header
{
  @include typography-h4;
  text-align: center;
}

.aoe-change-address-popup-content-option label
{
  @include typography-small;
}

.consultant-order-entry-div .single-line-auto-complete-row
{
  margin-top: 1rem !important;
}
.order-entry-div .k-overlay
{
 position: fixed !important;
}
</style>
